import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, addDoc, orderBy, limit, deleteDoc } from 'firebase/firestore';

const AdminTimesheet = ({ db, usersList }) => {
    const [viewYear, setViewYear] = useState(new Date().getFullYear());
    const [viewMonth, setViewMonth] = useState(new Date().getMonth());
    const [reportData, setReportData] = useState(null);
    const [isReportOpen, setIsReportOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [periodString, setPeriodString] = useState('');
    
    // History State
    const [savedReports, setSavedReports] = useState([]);
    const [expandedReportId, setExpandedReportId] = useState(null);

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    // --- DATE LOGIC ---
    // Fetch custom pay period or fall back to default
    const getCutoffDate = async (year, month) => {
        // Try fetch
        const docId = `period_${year}_${month}`;
        const snap = await getDoc(doc(db, "pay_periods", docId));
        
        if (snap.exists()) {
            return new Date(snap.data().cutoffDate);
        }

        // Default Logic (23rd Rule)
        const payday23 = new Date(year, month, 23);
        const dayOfWeek = payday23.getDay(); 
        let cutoffDay;
        if (dayOfWeek === 6) cutoffDay = 20; // Sat -> Wed
        else if (dayOfWeek === 0) cutoffDay = 19; // Sun -> Wed
        else if (dayOfWeek === 1) cutoffDay = 19; // Mon -> Thu (prev)
        else cutoffDay = 21; // Tue-Fri -> 21st

        return new Date(year, month, cutoffDay);
    };

    const getPayPeriodRange = async (year, month) => {
        const endDate = await getCutoffDate(year, month);
        // Start date is day after previous month's cutoff
        // Handle year rollover for previous month
        const prevDate = new Date(year, month - 1, 1);
        const prevMonthCutoff = await getCutoffDate(prevDate.getFullYear(), prevDate.getMonth());
        
        const startDate = new Date(prevMonthCutoff);
        startDate.setDate(startDate.getDate() + 1);
        
        return { startDate, endDate };
    };

    // --- CALCULATION LOGIC ---
    const calculateUnsocialHoursForShift = (startStr, finishStr) => {
        if (!startStr || !finishStr) return 0;
        const s = new Date(`1970-01-01T${startStr}`);
        const f = new Date(`1970-01-01T${finishStr}`);
        if (f < s) f.setDate(f.getDate() + 1); 

        const d1_00 = new Date(s); d1_00.setHours(0,0,0,0);
        const d1_06 = new Date(s); d1_06.setHours(6,0,0,0);
        const d1_20 = new Date(s); d1_20.setHours(20,0,0,0);
        const d1_24 = new Date(s); d1_24.setHours(24,0,0,0);
        const d2_00 = new Date(s); d2_00.setDate(d2_00.getDate() + 1); d2_00.setHours(0,0,0,0);
        const d2_06 = new Date(s); d2_06.setDate(d2_06.getDate() + 1); d2_06.setHours(6,0,0,0);

        const getOverlap = (start, end, wStart, wEnd) => {
            const maxStart = new Date(Math.max(start, wStart));
            const minEnd = new Date(Math.min(end, wEnd));
            return Math.max(0, (minEnd - maxStart) / (1000 * 60 * 60)); 
        };

        let unsoc = 0;
        unsoc += getOverlap(s, f, d1_00, d1_06);
        unsoc += getOverlap(s, f, d1_20, d1_24);
        unsoc += getOverlap(s, f, d2_00, d2_06);
        return unsoc;
    };

    const generateReport = async () => {
        setLoading(true);
        const { startDate, endDate } = await getPayPeriodRange(viewYear, viewMonth);
        setPeriodString(`${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`);

        // 1. Filter Users who need timesheets
        const targetUsers = usersList.filter(u => u.timesheetRequired);
        
        // 2. Fetch Timesheets (Current Month + Previous Month to cover overlap)
        const prevDate = new Date(viewYear, viewMonth - 1, 1);
        const prevYear = prevDate.getFullYear();
        const prevMonth = prevDate.getMonth();

        const dataMap = {}; 

        for (const user of targetUsers) {
            dataMap[user.initials] = {};
            
            const q1 = query(collection(db, "timesheets"), where("initials", "==", user.initials), where("year", "==", viewYear), where("month", "==", viewMonth));
            const s1 = await getDocs(q1);
            s1.forEach(doc => { const r = doc.data(); dataMap[user.initials][`${r.year}-${r.month}-${r.day}`] = r; });

            const q2 = query(collection(db, "timesheets"), where("initials", "==", user.initials), where("year", "==", prevYear), where("month", "==", prevMonth));
            const s2 = await getDocs(q2);
            s2.forEach(doc => { const r = doc.data(); dataMap[user.initials][`${r.year}-${r.month}-${r.day}`] = r; });
        }

        // 3. Calculate Totals
        const results = targetUsers.map(user => {
            const totals = {
                name: user.displayName,
                initials: user.initials,
                weeklyOvertime: 0,
                weekendOvertime: 0,
                unsoc: 0,
                travel: 0,
                overnight: 0
            };

            // Loop through every day in the pay period
            for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                const data = dataMap[user.initials]?.[key];

                if (data && data.start && data.finish) {
                    const s = new Date(`1970-01-01T${data.start}`);
                    const f = new Date(`1970-01-01T${data.finish}`);
                    let diffMs = f - s;
                    if (diffMs < 0) diffMs += 24 * 60 * 60 * 1000;
                    const durationHrs = diffMs / (1000 * 60 * 60);

                    // Unsocial
                    totals.unsoc += calculateUnsocialHoursForShift(data.start, data.finish);

                    // Travel
                    if (user.travelAllowance === 'Yes') totals.travel++;

                    // Overnights
                    if (data.isOvernight) totals.overnight++;

                    // Overtime
                    const dayOfWeek = d.getDay(); // 0=Sun
                    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

                    if (isWeekend) {
                        totals.weekendOvertime += durationHrs;
                    } else {
                        // Weekday Overtime
                        if (user.salaried !== 'Yes' && user.contractedHours) {
                            const contracted = parseFloat(user.contractedHours);
                            const lunchMins = parseFloat(user.defaultLunch) || 0;
                            const lunchHours = lunchMins / 60;
                            
                            const netWorked = durationHrs - lunchHours;
                            const dailyOvertime = netWorked - contracted;
                            if (dailyOvertime > 0) totals.weeklyOvertime += dailyOvertime;
                        }
                    }
                } else if (data && data.isOvernight) {
                    totals.overnight++;
                }
            }
            return totals;
        });

        setReportData(results);
        setLoading(false);
        setIsReportOpen(true);
    };

    // --- SAVE & PRINT ---
    const handlePrint = () => {
        const printWindow = window.open('', '', 'height=600,width=800');
        printWindow.document.write('<html><head><title>Timesheet Report</title>');
        printWindow.document.write('<style>table { width: 100%; border-collapse: collapse; } th, td { border: 1px solid black; padding: 8px; text-align: right; } th { text-align: left; background: #eee; }</style>');
        printWindow.document.write('</head><body>');
        printWindow.document.write(`<h2>Timesheet Report: ${monthNames[viewMonth]} ${viewYear}</h2>`);
        printWindow.document.write(`<p>Period: ${periodString}</p>`);
        printWindow.document.write(document.getElementById('report-table').outerHTML);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.print();
    };

    const handleSaveReport = async () => {
        if (!reportData) return;
        const confirmSave = window.confirm("Save this report to history?");
        if (!confirmSave) return;
        
        await addDoc(collection(db, "timesheet_reports"), {
            month: viewMonth,
            year: viewYear,
            periodString,
            data: reportData,
            savedAt: new Date()
        });
        alert("Report saved successfully.");
        fetchSavedReports();
    };

    const fetchSavedReports = async () => {
        const q = query(collection(db, "timesheet_reports"), orderBy("savedAt", "desc"), limit(20));
        const s = await getDocs(q);
        setSavedReports(s.docs.map(d => ({id: d.id, ...d.data()})));
    };

    const handleDeleteReport = async (id) => {
        if(!window.confirm("Delete this saved report?")) return;
        await deleteDoc(doc(db, "timesheet_reports", id));
        fetchSavedReports();
    };

    useEffect(() => {
        fetchSavedReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="card" style={{maxWidth:'1000px', margin:'0 auto'}}>
            <h3 style={{marginBottom:'20px'}}>Time Sheet Reporting</h3>
            
            <div style={{display:'flex', gap:'10px', alignItems:'center', justifyContent:'center', marginBottom:'20px'}}>
                <select value={viewMonth} onChange={e=>setViewMonth(Number(e.target.value))} style={{padding:'8px'}}>
                    {monthNames.map((m, i) => <option key={i} value={i}>{m}</option>)}
                </select>
                <select value={viewYear} onChange={e=>setViewYear(Number(e.target.value))} style={{padding:'8px'}}>
                    {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <button onClick={generateReport} disabled={loading} style={{background:'var(--primary)', color:'white', padding:'8px 16px'}}>
                    {loading ? 'Generating...' : 'Run Report'}
                </button>
            </div>

            {/* SAVED REPORTS LIST */}
            <div style={{marginTop:'30px', borderTop:'1px solid var(--border)', paddingTop:'15px'}}>
                <h4 style={{fontSize:'14px', color:'var(--text-muted)'}}>Saved Reports</h4>
                {savedReports.length === 0 && <p className="text-sm">No saved reports found.</p>}
                {savedReports.map(report => (
                    <div key={report.id} style={{marginBottom:'10px', border:'1px solid var(--border)', borderRadius:'8px', overflow:'hidden'}}>
                        <div 
                            onClick={() => setExpandedReportId(expandedReportId === report.id ? null : report.id)}
                            style={{padding:'10px 15px', background:'var(--light)', display:'flex', justifyContent:'space-between', cursor:'pointer', alignItems:'center'}}
                        >
                            <div>
                                <strong>{monthNames[report.month]} {report.year}</strong> 
                                <span style={{fontSize:'12px', color:'var(--text-muted)', marginLeft:'10px'}}>({report.periodString}) - Saved: {report.savedAt?.toDate().toLocaleDateString()}</span>
                            </div>
                            <button onClick={(e)=>{e.stopPropagation(); handleDeleteReport(report.id);}} style={{background:'transparent', color:'var(--danger)', fontSize:'12px', padding:'2px 6px', border:'1px solid var(--danger)'}}>Delete</button>
                        </div>
                        {expandedReportId === report.id && (
                            <div style={{padding:'10px', background:'var(--light)', borderTop:'1px solid var(--border)', overflowX:'auto'}}>
                                <table style={{width:'100%', fontSize:'12px', borderCollapse:'collapse', background:'var(--light)'}}>
                                    <thead>
                                        <tr style={{background:'var(--light)'}}>
                                            <th style={{padding:'8px', textAlign:'left'}}>Driver</th>
                                            <th style={{padding:'8px', textAlign:'right'}}>Weekly OT</th>
                                            <th style={{padding:'8px', textAlign:'right'}}>Weekend OT</th>
                                            <th style={{padding:'8px', textAlign:'right'}}>Unsoc</th>
                                            <th style={{padding:'8px', textAlign:'right'}}>Overnights</th>
                                            <th style={{padding:'8px', textAlign:'right'}}>Fuel (shifts)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {report.data.map((r, i) => (
                                            <tr key={i} style={{borderBottom:'1px solid #eee'}}>
                                                <td style={{padding:'8px'}}>{r.name}</td>
                                                <td style={{padding:'8px', textAlign:'right'}}>{r.weeklyOvertime?.toFixed(2)}</td>
                                                <td style={{padding:'8px', textAlign:'right'}}>{r.weekendOvertime?.toFixed(2)}</td>
                                                <td style={{padding:'8px', textAlign:'right'}}>{r.unsoc?.toFixed(2)}</td>
                                                <td style={{padding:'8px', textAlign:'right'}}>{r.overnight}</td>
                                                <td style={{padding:'8px', textAlign:'right'}}>{r.travel}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* REPORT MODAL */}
            {isReportOpen && reportData && (
                <div className="popup-overlay">
                    <div className="card animate-fade-in" style={{width:'95%', maxWidth:'900px', maxHeight:'90vh', overflowY:'auto', padding:'20px'}}>
                        <div className="flex-between" style={{marginBottom:'20px', borderBottom:'1px solid var(--border)', paddingBottom:'10px'}}>
                            <div>
                                <h3 style={{margin:0}}>Time Sheet Report: {monthNames[viewMonth]} {viewYear}</h3>
                                <p style={{margin:'5px 0 0 0', fontSize:'13px', color:'var(--text-muted)'}}>Period: {periodString}</p>
                            </div>
                            <button onClick={()=>setIsReportOpen(false)} style={{background:'transparent', fontSize:'18px', color:'var(--text-main)', padding:0}}>✕</button>
                        </div>

                        <div style={{display:'flex', gap:'10px', marginBottom:'15px', justifyContent:'flex-end'}}>
                             <button onClick={handlePrint} style={{background:'var(--dark)', color:'white', padding:'8px 12px'}}>Print Report</button>
                             <button onClick={handleSaveReport} style={{background:'var(--success)', color:'white', padding:'8px 12px'}}>Save Report</button>
                        </div>

                        <div style={{overflowX:'auto'}}>
                            <table id="report-table" style={{width:'100%', borderCollapse:'collapse', fontSize:'13px'}}>
                                <thead>
                                    <tr style={{background:'var(--light)', borderBottom:'2px solid var(--border)'}}>
                                        <th style={{padding:'12px', textAlign:'left'}}>Driver</th>
                                        <th style={{padding:'12px', textAlign:'right'}}>Weekly OT (hrs)</th>
                                        <th style={{padding:'12px', textAlign:'right'}}>Weekend OT (hrs)</th>
                                        <th style={{padding:'12px', textAlign:'right'}}>Unsocial (hrs)</th>
                                        <th style={{padding:'12px', textAlign:'right'}}>Overnights (qty)</th>
                                        <th style={{padding:'12px', textAlign:'right'}}>Fuel (shifts)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.map((row, idx) => (
                                        <tr key={idx} style={{borderBottom:'1px solid var(--border)'}}>
                                            <td style={{padding:'12px', fontWeight:'bold'}}>{row.name}</td>
                                            <td style={{padding:'12px', textAlign:'right', color:'#15803d'}}>{row.weeklyOvertime > 0 ? row.weeklyOvertime.toFixed(2) : '-'}</td>
                                            <td style={{padding:'12px', textAlign:'right', color:'#c2410c'}}>{row.weekendOvertime > 0 ? row.weekendOvertime.toFixed(2) : '-'}</td>
                                            <td style={{padding:'12px', textAlign:'right', color:'#6b21a8'}}>{row.unsoc > 0 ? row.unsoc.toFixed(2) : '-'}</td>
                                            <td style={{padding:'12px', textAlign:'right', color:'#7e22ce'}}>{row.overnight > 0 ? row.overnight : '-'}</td>
                                            <td style={{padding:'12px', textAlign:'right', color:'#0e7490'}}>{row.travel > 0 ? row.travel : '-'}</td>
                                        </tr>
                                    ))}
                                    {reportData.length === 0 && (
                                        <tr><td colSpan="6" style={{textAlign:'center', padding:'20px', fontStyle:'italic'}}>No drivers with 'Timesheet Required' found.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminTimesheet;